import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InteractiveTable from 'components/utils/InteractiveTable';
import { defaultPaginationOptions } from 'constants/pagination';
import { sizePerPageRenderer } from 'utils/commonUtils';
import { compose } from 'recompose';
import { MailingList, Exchange, ApiResponse } from 'constants/api';
import { getAllExchangesForCurrentScopeSelector } from 'redux/apiData/apiDataSelectors';
import { ReduxState } from 'createStore';
import { connect } from 'react-redux';
import ExchangeWithFlag from 'components/utils/Exchange';
import { Link } from 'react-router-dom';
import { getLinkToMailingList } from 'utils/linksUtils';
import { Roles } from 'constants/literals';
import { hasSomeRole } from 'utils/userUtils';
import withUserRoles from 'hocs/withUserRoles';
import { withApiData, ApiDataBinding } from 'react-api-data';
import DeleteModal from 'components/modals/DeleteModal';
import { get } from 'lodash';
import { EMAIL_LIST_DELIMITER } from 'constants/index';

interface SubscriptionInfoProps {
    url?: string;
    contactEmail?: string;
}

export function SubscriptionInfo(props: SubscriptionInfoProps) {
    const { url, contactEmail } = props;
    const { t } = useTranslation();

    if (url) {
        return (
            <a href={url} target="_blank">
                {url}
            </a>
        );
    } else if (contactEmail) {
        return (
            <div>
                <span className="mr-1">{t('mailingLists.contact')}</span>
                {contactEmail.split(EMAIL_LIST_DELIMITER).map((email) => {
                    const sanitizedEmail = email.trim();
                    return (
                        <span key={sanitizedEmail} className="email-list-item">
                            <a href={`mailto:${sanitizedEmail}`}>{sanitizedEmail}</a>
                        </span>
                    );
                })}
            </div>
        );
    } else {
        return null;
    }
}

interface InProps {
    mailingLists: MailingList[];
    searchTerm: string;
}

interface EnhanceProps {
    exchanges: Exchange[];
    userRoles: Roles[];
    deleteMailingListBinding: ApiDataBinding<ApiResponse<MailingList>>;
}

type Props = InProps & EnhanceProps;

interface TableData extends MailingList {
    exchange_name: string;
    exchange_short_name: string;
    contact_email?: string;
}

interface DeleteModalState {
    isOpen: boolean;
    mailingListData: TableData | null;
}

const enhance = compose<Props, InProps>(
    connect((state: ReduxState) => ({
        exchanges: getAllExchangesForCurrentScopeSelector(state.apiData),
    })),
    withUserRoles,
    withApiData({ deleteMailingListBinding: 'deleteMailingList' })
);

function composeTableData(mailingLists: MailingList[], exchanges: Exchange[]): TableData[] {
    const exchangesByUuid: { [key: string]: Exchange } = exchanges.reduce((memo: any, exchange) => {
        memo[exchange.uuid] = exchange;
        return memo;
    }, {});
    return mailingLists.map((mailingList) => {
        const exchange = exchangesByUuid[mailingList.exchange_uuid];
        return {
            ...mailingList,
            exchange_name: exchange.name,
            exchange_short_name: exchange.short_name,
        };
    });
}

function MailingListsList(props: Props) {
    const { mailingLists, exchanges, searchTerm, userRoles, deleteMailingListBinding } = props;
    const { t } = useTranslation();
    const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({
        isOpen: false,
        mailingListData: null,
    });
    const columns = useMemo(() => {
        const columns = [
            {
                dataField: 'name',
                text: t('mailingList.name'),
                sort: true,
            },
            {
                dataField: 'description',
                text: t('mailingList.description'),
                sort: true,
            },
            {
                dataField: 'list_url',
                text: t('mailingList.listUrl'),
                formatter: (_cell: any, row: TableData) => (
                    <SubscriptionInfo url={row.list_url} contactEmail={row.contact_email} />
                ),
            },
            {
                dataField: 'archives_url',
                text: t('mailingLists.archives'),
                formatter: (_cell: any, row: TableData) => {
                    if (!row.archives_url) {
                        return null;
                    }
                    return (
                        <a href={row.archives_url} target="_blank">
                            {row.archives_url}
                        </a>
                    );
                },
            },
            {
                dataField: 'exchange_short_name',
                text: t('common.exchange'),
                formatter: (cell: string) => <ExchangeWithFlag shortName={cell} />,
                sort: true,
            },
            {
                dataField: 'link',
                isDummyField: true,
                text: t('common.link'),
                formatter: (_cell: any, row: TableData) => (
                    <Link to={getLinkToMailingList(row.uuid)}>{t('common.view')}</Link>
                ),
            },
        ];
        if (hasSomeRole(userRoles, Roles.admin, Roles.noc)) {
            columns.push({
                dataField: 'delete',
                isDummyField: true,
                text: t('common.delete'),
                formatter: (_cell: any, row: TableData) => (
                    <span
                        data-test="mailing-list-delete-btn"
                        className="fas fa-trash fa-pull-left mt-1 text-primary cursor-pointer"
                        onClick={() => setDeleteModalState({ isOpen: true, mailingListData: row })}
                        title={t('mailingListDeleteModal.btnTooltip')}
                    />
                ),
            });
        }
        return columns;
    }, [t, userRoles, setDeleteModalState]);
    const data = useMemo(() => composeTableData(mailingLists, exchanges), [mailingLists, exchanges]);
    const filteredData = useMemo(() => {
        return data.filter((item) => {
            const searchableText = [item.name, item.description, item.exchange_name].join(' ');
            return searchableText.toLowerCase().includes(searchTerm.toLowerCase());
        });
    }, [data, searchTerm]);

    function deleteMailingList() {
        return deleteMailingListBinding.perform({ uuid: deleteModalState.mailingListData!.uuid });
    }

    return (
        <React.Fragment>
            <InteractiveTable
                classes="table-responsive-lg"
                keyField="uuid"
                columns={columns}
                data={filteredData}
                noDataIndication={() => t('common.noData')}
                defaultSorted={[
                    {
                        dataField: 'name',
                        order: 'asc',
                    },
                ]}
                paginationOptions={{
                    ...defaultPaginationOptions,
                    sizePerPageRenderer: sizePerPageRenderer('up'),
                }}
            />
            <DeleteModal
                header={t('mailingListDeleteModal.title')}
                description={t('mailingListDeleteModal.warningMessage', {
                    name: get(deleteModalState, 'mailingListData.name', ''),
                    exchange: get(deleteModalState, 'mailingListData.exchange_name', ''),
                })}
                successMessage={t('mailingListDeleteModal.successMessage')}
                isOpen={deleteModalState.isOpen}
                toggle={() => setDeleteModalState((state) => ({ ...state, isOpen: !state.isOpen }))}
                onSubmit={deleteMailingList}
            />
        </React.Fragment>
    );
}

export default enhance(MailingListsList);
